import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { BANCOS, acharBanco, buscarBancos, normalizarNome } from '../lib/bancos'
import { useDismiss } from '../layout/useDismiss'
import { BancoLogo } from './BancoLogo'
import { cn } from '../lib/utils'

interface Props {
  label?: string
  value: string
  onChange: (nome: string) => void
  placeholder?: string
}

/**
 * Campo de instituição com lista: dá para rolar e escolher ou digitar para filtrar. O que não estiver
 * na lista continua valendo como texto livre (é o mesmo campo `institution` de sempre).
 */
export function SeletorBanco({ label, value, onChange, placeholder = 'Buscar ou digitar o banco' }: Props) {
  const id = useId()
  const listaId = `${id}-lista`
  const raiz = useRef<HTMLDivElement>(null)
  const lista = useRef<HTMLUListElement>(null)
  const [aberto, setAberto] = useState(false)
  // Depois de escolher, a lista reabre completa; só filtra enquanto a pessoa digita.
  const [filtrando, setFiltrando] = useState(false)
  const [ativo, setAtivo] = useState(0)

  useDismiss(raiz, aberto, () => setAberto(false))

  const itens = useMemo(() => (filtrando ? buscarBancos(value) : BANCOS), [filtrando, value])
  const selecionado = acharBanco(value)

  useEffect(() => {
    if (aberto) lista.current?.children[ativo]?.scrollIntoView({ block: 'nearest' })
  }, [aberto, ativo])

  const escolher = (nome: string) => { onChange(nome); setFiltrando(false); setAberto(false) }

  const teclas = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setAberto(true); setAtivo((i) => Math.min(i + 1, itens.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAtivo((i) => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter' && aberto && itens[ativo]) { e.preventDefault(); escolher(itens[ativo].nome) }
    else if (e.key === 'Escape' && aberto) { e.stopPropagation(); setAberto(false) }
  }

  return (
    <div ref={raiz} className="relative">
      {label && <label htmlFor={id} className="form-label">{label}</label>}
      <div className="relative">
        {selecionado && <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2"><BancoLogo nome={value} size={24} /></span>}
        {!selecionado && <i className="ri-bank-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-muted" aria-hidden />}
        <input
          id={id} className="form-control pl-11" value={value} placeholder={placeholder} autoComplete="off"
          role="combobox" aria-expanded={aberto} aria-controls={listaId} aria-autocomplete="list"
          onFocus={() => { setAberto(true); setAtivo(0) }}
          onClick={() => setAberto(true)}
          onChange={(e) => { onChange(e.target.value); setFiltrando(true); setAberto(true); setAtivo(0) }}
          onKeyDown={teclas}
        />
      </div>

      {aberto && (
        <ul ref={lista} id={listaId} role="listbox" className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded border border-border bg-surface py-1 shadow-dropdown">
          {itens.length === 0 && (
            <li className="px-3 py-3 text-13 text-muted">
              {value.trim() ? <>Sem correspondência na lista. Vamos usar <strong className="text-white">{value.trim()}</strong> como digitado.</> : 'Nenhum banco na lista.'}
            </li>
          )}
          {itens.map((b, i) => (
            <li
              key={b.slug} role="option" aria-selected={normalizarNome(b.nome) === normalizarNome(value)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => escolher(b.nome)}
              onMouseEnter={() => setAtivo(i)}
              className={cn('flex cursor-pointer items-center gap-3 px-3 py-2 text-13 text-white', i === ativo && 'bg-surface2')}
            >
              <BancoLogo nome={b.nome} size={28} />
              <span className="flex-1 truncate">{b.nome}</span>
              {b.codigo && <span className="text-2xs text-muted">{b.codigo}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
